<template>
    <div class="file-list-wrapper">
        <!-- Success Toast Notification (Premium Feedback) -->
        <div v-if="successMessage" class="success-toast">
            <div class="toast-content">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="white" stroke-width="3">
                    <polyline points="20 6 9 17 4 12"></polyline>
                </svg>
                <span>{{ successMessage }}</span>
            </div>
        </div>

        <!-- Error Toast Notification -->
        <div v-if="errorMessage" class="error-toast">
            <div class="toast-content error">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="white" stroke-width="2.5">
                    <circle cx="12" cy="12" r="10" />
                    <line x1="12" y1="8" x2="12" y2="12" />
                    <line x1="12" y1="16" x2="12.01" y2="16" />
                </svg>
                <span>{{ errorMessage }}</span>
                <button class="toast-close-btn" @click="errorMessage = ''">
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="white" stroke-width="2.5"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
                </button>
            </div>
        </div>
        <!-- Breadcrumb & Header -->
        <div class="breadcrumb-cd">
            <router-link to="/companies">Companies</router-link>
            <span class="crumb-sep">/</span>
            <span class="crumb-current">{{ company.name }}</span>
        </div>

        <div class="cd-header">
            <div>
                <h1 class="cd-title">{{ company.name }}</h1>
                <p class="cd-subtitle">Code: <span class="text-id">#{{ company.code }}</span> | Admin: {{
                    company.contact_person_name }}</p>
            </div>
            <button v-if="activeTab === 'licenses'" class="btn-generate-licenses" @click="showModal = true">
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5">
                    <line x1="12" y1="5" x2="12" y2="19" />
                    <line x1="5" y1="12" x2="19" y2="12" />
                </svg>
                Generate Licenses
            </button>
            <button v-if="activeTab === 'users'" class="btn-generate-licenses" @click="openCreateUser">
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5">
                    <path d="M16 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
                    <circle cx="8.5" cy="7" r="4" />
                    <line x1="20" y1="8" x2="20" y2="14" />
                    <line x1="17" y1="11" x2="23" y2="11" />
                </svg>
                Create User
            </button>
        </div>

        <!-- Tab Navigation -->
        <div class="tabs-dock-cd">
            <button @click="activeTab = 'users'" :class="['tab-dock-btn-cd', { active: activeTab === 'users' }]">
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5"><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/></svg>
                Users
            </button>
            <button @click="activeTab = 'licenses'" :class="['tab-dock-btn-cd', { active: activeTab === 'licenses' }]">
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5"><rect x="3" y="11" width="18" height="11" rx="2" ry="2"/><path d="M7 11V7a5 5 0 0 1 10 0v4"/></svg>
                Licenses
            </button>
        </div>

        <!-- Users Table -->
        <div v-if="activeTab === 'users'" class="table-container">
            <div class="table-header-box"
                style="padding: 1.25rem 1.5rem; background: #f8fafc; border-bottom: 1px solid #e2e8f0; border-radius: 12px 12px 0 0; display: flex; justify-content: space-between; align-items: center;">
                <h3 style="margin: 0; font-size: 1rem; color: #475569; font-weight: 600;">Registered Users ({{
                    users.length }})</h3>
            </div>
            <table class="file-table">
                <thead>
                    <tr>
                        <th>User ID</th>
                        <th>Full Name</th>
                        <th>Email Address</th>
                        <th>Mobile</th>
                        <th>Role</th>
                        <th>Status</th>
                        <th>Action</th>
                    </tr>
                </thead>
                <tbody>
                    <tr v-for="user in users" :key="user.id">
                        <td><span class="text-id">#USR-{{ user.id }}</span></td>
                        <td><span style="font-weight: 600; color: #0f172a;">{{ user.name }}</span></td>
                        <td><span style="color: #64748b;">{{ user.email }}</span></td>
                        <td><span style="color: #64748b;">{{ user.mobile || '—' }}</span></td>
                        <td>
                            <select 
                                :disabled="loading"
                                @change="quickUpdateRole(user, $event.target.value)"
                                style="padding: 6px 10px; border-radius: 8px; border: 1px solid #e2e8f0; font-size: 0.75rem; background: #f8fafc; cursor: pointer; color: #475569; font-weight: 700; transition: all 0.2s; outline: none;"
                                onmouseover="this.style.borderColor='#0071C5'; this.style.background='white'"
                                onmouseout="this.style.borderColor='#e2e8f0'; this.style.background='#f8fafc'"
                            >
                                <option v-for="role in availableRoles" :key="role.id" :value="role.name" :selected="user.roles && user.roles.some(r => r.name === role.name)">
                                    {{ role.name.toUpperCase() }}
                                </option>
                            </select>
                        </td>
                        <td>
                            <span class="status-badge" :style="{
                                background: user.is_active ? 'rgba(16, 185, 129, 0.1)' : 'rgba(239, 68, 68, 0.1)',
                                color: user.is_active ? '#10b981' : '#ef4444'
                            }">
                                {{ user.is_active ? 'Active' : 'Inactive' }}
                            </span>
                        </td>
                        <td>
                            <button class="btn-icon" title="Edit User" @click="editUser(user)"
                                style="background: none; border: none; color: #0071C5; cursor: pointer;">
                                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor"
                                    stroke-width="2">
                                    <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
                                    <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
                                </svg>
                            </button>
                        </td>
                    </tr>
                    <tr v-if="users.length === 0">
                        <td colspan="6" class="text-center py-12" style="padding: 4rem 0;">
                            <div class="empty-state">
                                <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="#cbd5e1"
                                    stroke-width="1.5" style="margin-bottom: 1rem;">
                                    <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
                                    <circle cx="9" cy="7" r="4" />
                                    <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
                                    <path d="M16 3.13a4 4 0 0 1 0 7.75" />
                                </svg>
                                <p class="text-gray-400">No users registered for this company yet.</p>
                            </div>
                        </td>
                    </tr>
                </tbody>
            </table>
            <AppPagination :currentPage="usersPage" :totalPages="usersTotalPages" @page-changed="fetchUsers" />
        </div>

        <!-- Licenses Table (Existing) -->
        <div v-if="activeTab === 'licenses'" class="table-container">
            <div class="table-header-box"
                style="padding: 1rem; background: #f8fafc; border-bottom: 1px solid #e2e8f0; border-radius: 12px 12px 0 0;">
                <h3 style="margin: 0; font-size: 1rem; color: #475569;">Licenses & User Assignments ({{ licenses.length
                }})</h3>
            </div>
            <table class="file-table">
                <thead>
                    <tr>
                        <th>License Key</th>
                        <th>Generated On</th>
                        <th>User Assignment</th>
                        <th>Used On</th>
                        <th>Status</th>
                        <th>Action</th>
                    </tr>
                </thead>
                <tbody>
                    <tr v-for="license in licenses" :key="license.id">
                        <td><span class="text-id" style="font-size: 1rem; color: #0071C5; font-weight: 700;">{{
                            license.license }}</span></td>
                        <td><span class="text-date text-sm">{{ formatDate(license.created_date) }}</span></td>
                        <td>
                            <div v-if="license.user" style="display:flex; flex-direction:column;">
                                <span style="font-weight:600; font-size:0.9rem; color: #0f172a;">{{ license.user.name
                                }}</span>
                                <span style="font-size:0.8rem; color:#64748b;">{{ license.user.email }}</span>
                            </div>
                            <span v-else class="text-placeholder" style="color:#cbd5e1; font-style: italic;">— Available
                                —</span>
                        </td>
                        <td><span class="text-date text-sm">{{ formatDate(license.used_date) }}</span></td>
                        <td>
                            <span class="status-badge" :style="{
                                background: license.is_active ? 'rgba(16, 185, 129, 0.1)' : 'rgba(239, 68, 68, 0.1)',
                                color: license.is_active ? '#10b981' : '#ef4444'
                            }">
                                {{ license.is_active ? 'Unused' : 'Used' }}
                            </span>
                        </td>
                        <td>
                            <button v-if="license.is_active" class="btn-icon" title="Archive Key"
                                @click="deleteLicense(license.id)"
                                style="background: none; border: none; color: #94a3b8; cursor: pointer;">
                                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor"
                                    stroke-width="2">
                                    <path
                                        d="M3 6h18m-2 0v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
                                </svg>
                            </button>
                        </td>
                    </tr>
                    <tr v-if="licenses.length === 0">
                        <td colspan="6" class="text-center py-12">
                            <div class="empty-state">
                                <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="#cbd5e1"
                                    stroke-width="1.5" style="margin-bottom: 1rem;">
                                    <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
                                    <path d="M7 11V7a5 5 0 0 1 10 0v4" />
                                </svg>
                                <p class="text-gray-400">No licenses found. Generate keys to get started.</p>
                            </div>
                        </td>
                    </tr>
                </tbody>
            </table>
            <AppPagination :currentPage="licensesPage" :totalPages="licensesTotalPages" @page-changed="fetchLicenses" />
        </div>

        <!-- Add License Modal -->
        <div v-if="showModal" class="modal-overlay" @click.self="showModal = false">
            <div class="modal-content glass-card">
                <div class="modal-header">
                    <div style="display: flex; flex-direction: column;">
                        <h3 style="margin: 0; font-size: 1.25rem; color: #0f172a; font-weight: 700;">Generate Licenses
                        </h3>
                        <p style="margin: 4px 0 0 0; font-size: 0.85rem; color: #64748b;">For {{ company.name }}</p>
                    </div>
                    <button class="close-btn" @click="showModal = false">&times;</button>
                </div>

                <form @submit.prevent="generateLicenses" style="margin-top: 1.5rem;">
                    <div class="form-group">
                        <label class="form-label"
                            style="display: block; margin-bottom: 0.5rem; font-weight: 600; color: #475569; font-size: 0.9rem;">Number
                            of Licenses</label>
                        <div style="position: relative;">
                            <input type="number" v-model="licenseCount" class="form-input" min="1" max="100" required
                                style="width: 100%; border: 1px solid #e2e8f0; border-radius: 10px; padding: 0.75rem 1rem; font-size: 1rem; transition: all 0.2s; outline: none;">
                        </div>
                    </div>

                    <div class="form-group">
                        <label class="form-label"
                            style="display: block; margin-bottom: 0.5rem; font-weight: 600; color: #475569; font-size: 0.9rem;">Assign
                            Role</label>
                        <div style="position: relative;">
                            <select v-model="licenseRole" required
                                style="width: 100%; border: 1px solid #e2e8f0; border-radius: 10px; padding: 0.75rem 1rem; font-size: 1rem; transition: all 0.2s; outline: none; background: #fff; cursor: pointer;">
                                <option :value="2">USER (Standard Access)</option>
                                <option :value="1">ADMIN (Company Manager)</option>
                            </select>
                        </div>
                        <p class="text-sm mt-3"
                            style="color: #64748b; font-size: 0.8rem; display: flex; align-items: center; gap: 6px;">
                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor"
                                stroke-width="2">
                                <circle cx="12" cy="12" r="10" />
                                <line x1="12" y1="16" x2="12" y2="12" />
                                <line x1="12" y1="8" x2="12.01" y2="8" />
                            </svg>
                            Each key allows one unique user to register and access.
                        </p>
                    </div>

                    <div class="modal-actions"
                        style="margin-top: 2rem; display: flex; justify-content: flex-end; gap: 12px;">
                        <button type="button" class="btn-secondary" @click="showModal = false"
                            style="flex: 1; border: 1px solid #e2e8f0; border-radius: 10px; padding: 0.75rem; font-weight: 600; color: #64748b; background: #fff; cursor: pointer;">
                            Cancel
                        </button>
                        <button type="submit" class="btn-primary" :disabled="loading"
                            style="flex: 2; border: none; border-radius: 10px; padding: 0.75rem; font-weight: 700; color: #fff; background: #0071C5; cursor: pointer; display: flex; align-items: center; justify-content: center; gap: 8px;">
                            <span v-if="loading" class="spinner-small"
                                style="width: 16px; height: 16px; border: 2px solid rgba(255,255,255,0.3); border-top-color: #fff; border-radius: 50%; display: inline-block; animation: spin 0.8s linear infinite;"></span>
                            {{ loading ? 'Generating...' : 'Confirm Generation' }}
                        </button>
                    </div>
                </form>
            </div>
        </div>

        <!-- Create User Modal (Super Admin — no OTP verification) -->
        <div v-if="showCreateUserModal" class="modal-overlay" @click.self="showCreateUserModal = false">
            <div class="modal-content glass-card" style="max-width: 600px; padding: 0; overflow: hidden; border: none; margin: 2rem;">
                <div
                    style="background: linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%); padding: 1.5rem 2rem; border-bottom: 1px solid #e2e8f0; display: flex; align-items: center; gap: 1rem;">
                    <div
                        style="background: white; width: 48px; height: 48px; border-radius: 12px; display: flex; align-items: center; justify-content: center; box-shadow: 0 4px 10px rgba(0,0,0,0.05); flex-shrink: 0; border: 1px solid #e2e8f0;">
                        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#0071C5" stroke-width="2">
                            <path d="M16 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
                            <circle cx="8.5" cy="7" r="4" />
                            <line x1="20" y1="8" x2="20" y2="14" />
                            <line x1="17" y1="11" x2="23" y2="11" />
                        </svg>
                    </div>
                    <div>
                        <h2 style="color: #0f172a; font-size: 1.25rem; font-weight: 800; margin: 0; letter-spacing: -0.01em;">
                            Create User</h2>
                        <p style="color: #64748b; font-size: 0.8rem; margin: 0.15rem 0 0 0; font-weight: 500;">For <span
                                style="color: #0071C5; font-weight: 600;">{{ company.name }}</span> — no OTP verification
                            required</p>
                    </div>
                </div>

                <div style="padding: 1.5rem 2rem;">
                    <!-- License notice: role comes from the license that will be consumed -->
                    <div v-if="availableLicense"
                        style="margin-bottom: 1.25rem; padding: 0.85rem 1rem; background: rgba(0,113,197,0.06); border: 1px solid rgba(0,113,197,0.18); border-radius: 10px; display: flex; align-items: center; gap: 10px;">
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#0071C5" stroke-width="2"
                            style="flex-shrink: 0;">
                            <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
                            <path d="M7 11V7a5 5 0 0 1 10 0v4" />
                        </svg>
                        <span style="font-size: 0.82rem; color: #475569;">
                            License <strong style="color: #0071C5; font-weight: 700;">{{ availableLicense.license
                            }}</strong> will be used — role is taken from this key.
                        </span>
                    </div>
                    <div v-else
                        style="margin-bottom: 1.25rem; padding: 0.85rem 1rem; background: rgba(239,68,68,0.06); border: 1px solid rgba(239,68,68,0.18); border-radius: 10px; display: flex; align-items: center; gap: 10px;">
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#ef4444" stroke-width="2"
                            style="flex-shrink: 0;">
                            <circle cx="12" cy="12" r="10" />
                            <line x1="12" y1="8" x2="12" y2="12" />
                            <line x1="12" y1="16" x2="12.01" y2="16" />
                        </svg>
                        <span style="font-size: 0.82rem; color: #475569;">
                            No unused license available. Generate a license first from the
                            <strong style="font-weight: 700;">Licenses</strong> tab.
                        </span>
                    </div>

                    <form @submit.prevent="createUser" style="display: flex; flex-direction: column; gap: 1.25rem;">
                        <div style="display: grid; grid-template-columns: 1fr 1fr; gap: 1rem;">
                            <div style="display: flex; flex-direction: column; gap: 0.4rem;">
                                <label style="font-weight: 600; color: #475569; font-size: 0.8rem; margin-left: 2px;">Full
                                    Name</label>
                                <input type="text" v-model="newUser.name" required placeholder="Enter full name"
                                    style="width: 100%; padding: 0.75rem 1rem; border: 1.5px solid #e2e8f0; border-radius: 10px; font-size: 0.9rem; outline: none; transition: all 0.2s; background: #fff; box-sizing: border-box;"
                                    onfocus="this.style.borderColor='#0071C5'; this.style.boxShadow='0 0 0 4px rgba(0,113,197,0.1)'"
                                    onblur="this.style.borderColor='#e2e8f0'; this.style.boxShadow='none'">
                            </div>

                            <div style="display: flex; flex-direction: column; gap: 0.4rem;">
                                <label style="font-weight: 600; color: #475569; font-size: 0.8rem; margin-left: 2px;">Email
                                    Address</label>
                                <input type="email" v-model="newUser.email" required placeholder="name@company.com"
                                    style="width: 100%; padding: 0.75rem 1rem; border: 1.5px solid #e2e8f0; border-radius: 10px; font-size: 0.9rem; outline: none; transition: all 0.2s; background: #fff; box-sizing: border-box;"
                                    onfocus="this.style.borderColor='#0071C5'; this.style.boxShadow='0 0 0 4px rgba(0,113,197,0.1)'"
                                    onblur="this.style.borderColor='#e2e8f0'; this.style.boxShadow='none'">
                            </div>
                        </div>

                        <div style="display: flex; flex-direction: column; gap: 0.4rem;">
                            <label style="font-weight: 600; color: #475569; font-size: 0.8rem; margin-left: 2px;">Mobile
                                Number</label>
                            <input type="text" v-model="newUser.mobile" required placeholder="10-digit mobile number"
                                style="width: 100%; padding: 0.75rem 1rem; border: 1.5px solid #e2e8f0; border-radius: 10px; font-size: 0.9rem; outline: none; transition: all 0.2s; background: #fff; box-sizing: border-box;"
                                onfocus="this.style.borderColor='#0071C5'; this.style.boxShadow='0 0 0 4px rgba(0,113,197,0.1)'"
                                onblur="this.style.borderColor='#e2e8f0'; this.style.boxShadow='none'">
                        </div>

                        <div style="display: grid; grid-template-columns: 1fr 1fr; gap: 1rem;">
                            <div style="display: flex; flex-direction: column; gap: 0.4rem;">
                                <label style="font-weight: 600; color: #475569; font-size: 0.8rem; margin-left: 2px;">Password</label>
                                <input :type="showNewPassword ? 'text' : 'password'" v-model="newUser.password" required
                                    placeholder="Set a password"
                                    style="width: 100%; padding: 0.75rem 1rem; border: 1.5px solid #e2e8f0; border-radius: 10px; font-size: 0.9rem; outline: none; transition: all 0.2s; background: #fff; box-sizing: border-box;"
                                    onfocus="this.style.borderColor='#0071C5'; this.style.boxShadow='0 0 0 4px rgba(0,113,197,0.1)'"
                                    onblur="this.style.borderColor='#e2e8f0'; this.style.boxShadow='none'">
                            </div>

                            <div style="display: flex; flex-direction: column; gap: 0.4rem;">
                                <label style="font-weight: 600; color: #475569; font-size: 0.8rem; margin-left: 2px;">Confirm
                                    Password</label>
                                <input :type="showNewPassword ? 'text' : 'password'" v-model="newUser.confirmPassword"
                                    required placeholder="Re-enter password"
                                    style="width: 100%; padding: 0.75rem 1rem; border: 1.5px solid #e2e8f0; border-radius: 10px; font-size: 0.9rem; outline: none; transition: all 0.2s; background: #fff; box-sizing: border-box;"
                                    onfocus="this.style.borderColor='#0071C5'; this.style.boxShadow='0 0 0 4px rgba(0,113,197,0.1)'"
                                    onblur="this.style.borderColor='#e2e8f0'; this.style.boxShadow='none'">
                            </div>
                        </div>

                        <label
                            style="display: flex; align-items: center; gap: 8px; font-size: 0.8rem; color: #64748b; cursor: pointer; margin-top: -0.5rem;">
                            <input type="checkbox" v-model="showNewPassword" style="cursor: pointer;">
                            Show password
                        </label>

                        <div v-if="createUserError"
                            style="padding: 0.75rem 1rem; background: rgba(239,68,68,0.08); border: 1px solid rgba(239,68,68,0.2); border-radius: 10px; color: #ef4444; font-size: 0.82rem; font-weight: 600;">
                            {{ createUserError }}
                        </div>

                        <div style="margin-top: 0.5rem; display: flex; justify-content: flex-end; gap: 12px;">
                            <button type="button" class="btn-secondary" @click="showCreateUserModal = false"
                                style="flex: 1; border: 1px solid #e2e8f0; border-radius: 10px; padding: 0.75rem; font-weight: 600; color: #64748b; background: #fff; cursor: pointer;">
                                Cancel
                            </button>
                            <button type="submit" class="btn-primary" :disabled="loading || !availableLicense"
                                :style="{ flex: 2, border: 'none', borderRadius: '10px', padding: '0.75rem', fontWeight: 700, color: '#fff', background: (loading || !availableLicense) ? '#94a3b8' : '#0071C5', cursor: (loading || !availableLicense) ? 'not-allowed' : 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px' }">
                                <span v-if="loading" class="spinner-small"
                                    style="width: 16px; height: 16px; border: 2px solid rgba(255,255,255,0.3); border-top-color: #fff; border-radius: 50%; display: inline-block; animation: spin 0.8s linear infinite;"></span>
                                {{ loading ? 'Creating...' : 'Create User' }}
                            </button>
                        </div>
                    </form>
                </div>
            </div>
        </div>

        <!-- Edit User Modal -->
        <div v-if="showEditUserModal" class="modal-overlay" @click.self="showEditUserModal = false">
            <div class="modal-content glass-card"
                style="max-width: 600px; padding: 0; overflow: hidden; border: none; margin: 2rem;">
                <!-- Premium Modal Header -->
                <div
                    style="background: linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%); padding: 1.5rem 2rem; border-bottom: 1px solid #e2e8f0; display: flex; align-items: center; gap: 1rem;">
                    <div
                        style="background: white; width: 48px; height: 48px; border-radius: 12px; display: flex; align-items: center; justify-content: center; box-shadow: 0 4px 10px rgba(0,0,0,0.05); flex-shrink: 0; border: 1px solid #e2e8f0;">
                        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#0071C5" stroke-width="2">
                            <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
                            <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
                        </svg>
                    </div>
                    <div>
                        <h2
                            style="color: #0f172a; font-size: 1.25rem; font-weight: 800; margin: 0; letter-spacing: -0.01em;">
                            Edit User Details</h2>
                        <p style="color: #64748b; font-size: 0.8rem; margin: 0.15rem 0 0 0; font-weight: 500;">Modifying
                            account for <span style="color: #0071C5; font-weight: 600;">#USR-{{ selectedUser.id
                                }}</span></p>
                    </div>
                </div>

                <div style="padding: 1.5rem 2rem;">
                    <form @submit.prevent="updateUser" style="display: flex; flex-direction: column; gap: 1.25rem;">
                        <!-- Name & Email (Two Columns) -->
                        <div style="display: grid; grid-template-columns: 1fr 1fr; gap: 1rem;">
                            <div style="display: flex; flex-direction: column; gap: 0.4rem;">
                                <label
                                    style="font-weight: 600; color: #475569; font-size: 0.8rem; margin-left: 2px;">Full
                                    Name</label>
                                <input type="text" v-model="selectedUser.name" required placeholder="Enter full name"
                                    style="width: 100%; padding: 0.75rem 1rem; border: 1.5px solid #e2e8f0; border-radius: 10px; font-size: 0.9rem; outline: none; transition: all 0.2s; background: #fff; box-sizing: border-box;"
                                    onfocus="this.style.borderColor='#0071C5'; this.style.boxShadow='0 0 0 4px rgba(0,113,197,0.1)'"
                                    onblur="this.style.borderColor='#e2e8f0'; this.style.boxShadow='none'">
                            </div>

                            <div style="display: flex; flex-direction: column; gap: 0.4rem;">
                                <label
                                    style="font-weight: 600; color: #475569; font-size: 0.8rem; margin-left: 2px;">Email
                                    Address</label>
                                <input type="email" v-model="selectedUser.email" required placeholder="name@company.com"
                                    style="width: 100%; padding: 0.75rem 1rem; border: 1.5px solid #e2e8f0; border-radius: 12px; font-size: 0.9rem; outline: none; transition: all 0.2s; background: #fff; box-sizing: border-box;"
                                    onfocus="this.style.borderColor='#0071C5'; this.style.boxShadow='0 0 0 4px rgba(0,113,197,0.1)'"
                                    onblur="this.style.borderColor='#e2e8f0'; this.style.boxShadow='none'">
                            </div>
                        </div>

                        <!-- Mobile & Role -->
                        <div style="display: grid; grid-template-columns: 1fr 1fr; gap: 1rem;">
                            <div style="display: flex; flex-direction: column; gap: 0.4rem;">
                                <label
                                    style="font-weight: 600; color: #475569; font-size: 0.8rem; margin-left: 2px;">Mobile
                                    Number</label>
                                <input type="text" v-model="selectedUser.mobile" placeholder="Enter mobile number"
                                    style="width: 100%; padding: 0.75rem 1rem; border: 1.5px solid #e2e8f0; border-radius: 10px; font-size: 0.9rem; outline: none; transition: all 0.2s; background: #fff; box-sizing: border-box;"
                                    onfocus="this.style.borderColor='#0071C5'; this.style.boxShadow='0 0 0 4px rgba(0,113,197,0.1)'"
                                    onblur="this.style.borderColor='#e2e8f0'; this.style.boxShadow='none'">
                            </div>

                            <div style="display: flex; flex-direction: column; gap: 0.4rem;">
                                <label
                                    style="font-weight: 600; color: #475569; font-size: 0.8rem; margin-left: 2px;">Assign
                                    Role</label>
                                <select v-model="selectedUser.role"
                                    style="width: 100%; padding: 0.75rem 1rem; border: 1.5px solid #e2e8f0; border-radius: 10px; font-size: 0.9rem; outline: none; transition: all 0.2s; background: #fff; cursor: pointer;"
                                    onfocus="this.style.borderColor='#0071C5'; this.style.boxShadow='0 0 0 4px rgba(0,113,197,0.1)'"
                                    onblur="this.style.borderColor='#e2e8f0'; this.style.boxShadow='none'">
                                    <option v-for="role in availableRoles" :key="role.id" :value="role.name">
                                        {{ role.name.toUpperCase() }}
                                    </option>
                                </select>
                            </div>
                        </div>

                        <!-- Password Reset -->
                        <div
                            style="background: #f1f5f9; padding: 1.25rem; border-radius: 12px; border: 1px solid #e2e8f0;">
                            <label
                                style="font-weight: 700; color: #1e293b; font-size: 0.8rem; display: flex; align-items: center; gap: 8px; margin-bottom: 0.75rem;">
                                <div
                                    style="background: #0071C5; width: 22px; height: 22px; border-radius: 6px; display: flex; align-items: center; justify-content: center; flex-shrink: 0;">
                                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="white"
                                        stroke-width="2.5">
                                        <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
                                        <path d="M7 11V7a5 5 0 0 1 10 0v4" />
                                    </svg>
                                </div>
                                Security Update (Optional)
                            </label>

                            <!-- New Password -->
                            <div style="position: relative; display: flex; align-items: center; margin-bottom: 1rem;">
                                <input :type="showPassword ? 'text' : 'password'" v-model="selectedUser.password"
                                    placeholder="Set new password"
                                    style="width: 100%; padding: 0.85rem 3rem 0.85rem 1rem; border: 1.5px solid #cbd5e1; border-radius: 10px; font-size: 0.95rem; outline: none; background: white; transition: all 0.2s;"
                                    onfocus="this.style.borderColor='#0071C5'; this.style.boxShadow='0 0 0 4px rgba(0,113,197,0.05)'"
                                    onblur="this.style.borderColor='#cbd5e1'; this.style.boxShadow='none'">

                                <button type="button" @click="showPassword = !showPassword"
                                    style="position: absolute; right: 12px; background: none; border: none; cursor: pointer; color: #64748b; padding: 4px; display: flex; align-items: center; justify-content: center; transition: color 0.2s;"
                                    @mouseover="$event.target.style.color = '#0071C5'"
                                    @mouseleave="$event.target.style.color = '#64748b'">
                                    <svg v-if="!showPassword" width="20" height="20" viewBox="0 0 24 24" fill="none"
                                        stroke="currentColor" stroke-width="2">
                                        <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
                                        <circle cx="12" cy="12" r="3" />
                                    </svg>
                                    <svg v-else width="20" height="20" viewBox="0 0 24 24" fill="none"
                                        stroke="currentColor" stroke-width="2">
                                        <path
                                            d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24" />
                                        <line x1="1" y1="1" x2="23" y2="23" />
                                    </svg>
                                </button>
                            </div>

                            <!-- Confirm Password -->
                            <div
                                style="position: relative; display: flex; align-items: center; margin-bottom: 0.25rem;">
                                <input :type="showPassword ? 'text' : 'password'" v-model="selectedUser.confirmPassword"
                                    placeholder="Confirm new password"
                                    style="width: 100%; padding: 0.85rem 3rem 0.85rem 1rem; border: 1.5px solid #cbd5e1; border-radius: 10px; font-size: 0.95rem; outline: none; background: white; transition: all 0.2s;"
                                    :style="selectedUser.password && selectedUser.confirmPassword && selectedUser.password !== selectedUser.confirmPassword ? 'border-color: #ef4444; background: #fffafb;' : ''"
                                    onfocus="this.style.borderColor='#0071C5'; this.style.boxShadow='0 0 0 4px rgba(0,113,197,0.05)'"
                                    onblur="this.style.borderColor='#cbd5e1'; this.style.boxShadow='none'">

                                <div
                                    style="position: absolute; right: 12px; display: flex; align-items: center; gap: 8px;">
                                    <svg v-if="selectedUser.password && selectedUser.confirmPassword && selectedUser.password === selectedUser.confirmPassword"
                                        width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#22c55e"
                                        stroke-width="3">
                                        <polyline points="20 6 9 17 4 12"></polyline>
                                    </svg>
                                    <svg v-else-if="selectedUser.password && selectedUser.confirmPassword && selectedUser.password !== selectedUser.confirmPassword"
                                        width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#ef4444"
                                        stroke-width="3">
                                        <line x1="18" y1="6" x2="6" y2="18"></line>
                                        <line x1="6" y1="6" x2="18" y2="18"></line>
                                    </svg>
                                </div>
                            </div>
                            <p v-if="selectedUser.password && selectedUser.confirmPassword && selectedUser.password !== selectedUser.confirmPassword"
                                style="color: #ef4444; font-size: 0.7rem; margin: 4px 0 10px 4px; font-weight: 600;">
                                Passwords do not match</p>

                            <!-- Strength Meter (Matching User Panel) -->
                            <div v-if="selectedUser.password" style="margin-top: 10px;">
                                <div
                                    style="display: flex; align-items: center; justify-content: space-between; margin-bottom: 6px;">
                                    <span
                                        style="font-size: 0.75rem; font-weight: 700; text-transform: uppercase; letter-spacing: 0.5px;"
                                        :style="{ color: strengthColor }">
                                        Strength: {{ strengthLabel }}
                                    </span>
                                    <span style="font-size: 0.75rem; color: #64748b; font-weight: 500;">{{
                                        strengthPercent }}%</span>
                                </div>
                                <div
                                    style="width: 100%; height: 6px; background: #e2e8f0; border-radius: 10px; overflow: hidden;">
                                    <div :style="{ width: strengthPercent + '%', background: strengthColor }"
                                        style="height: 100%; transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1); border-radius: 10px;">
                                    </div>
                                </div>
                                <p
                                    style="font-size: 0.72rem; color: #64748b; margin-top: 8px; line-height: 1.4; font-weight: 500;">
                                    Use 8+ characters with letters, numbers & symbols for a strong password.
                                </p>
                            </div>
                        </div>

                        <!-- Error Message Alert -->
                        <div v-if="validationError"
                            style="background: #fef2f2; border: 1px solid #fee2e2; padding: 0.85rem; border-radius: 10px; display: flex; align-items: center; gap: 10px; animation: shake 0.5s cubic-bezier(.36,.07,.19,.97) both;">
                            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#ef4444"
                                stroke-width="2.5">
                                <circle cx="12" cy="12" r="10" />
                                <line x1="12" y1="8" x2="12" y2="12" />
                                <line x1="12" y1="16" x2="12.01" y2="16" />
                            </svg>
                            <span style="color: #991b1b; font-size: 0.82rem; font-weight: 600;">{{ validationError
                            }}</span>
                        </div>

                        <!-- Footer Actions -->
                        <div style="margin-top: 0.5rem; display: flex; gap: 12px;">
                            <button type="button" class="btn-secondary" @click="showEditUserModal = false"
                                style="flex: 1; border: 1.5px solid #e2e8f0; border-radius: 12px; padding: 0.85rem; font-weight: 600; color: #475569; background: #fff; cursor: pointer; transition: all 0.2s;">
                                Cancel
                            </button>
                            <button type="submit" class="btn-primary" :disabled="loading"
                                style="flex: 2; border: none; border-radius: 12px; padding: 0.85rem; font-weight: 700; color: #fff; background: #0071C5; cursor: pointer; display: flex; align-items: center; justify-content: center; gap: 10px; box-shadow: 0 4px 12px rgba(0, 113, 197, 0.2); transition: all 0.2s;">
                                <span v-if="loading" class="spinner-small"
                                    style="width: 18px; height: 18px; border: 2px solid rgba(255,255,255,0.3); border-top-color: #fff; border-radius: 50%; display: inline-block; animation: spin 0.8s linear infinite;"></span>
                                {{ loading ? 'Saving...' : 'SAVE CHANGES' }}
                            </button>
                        </div>
                    </form>
                </div>
            </div>
        </div>
    </div>

    <!-- Delete Confirmation Modal -->
    <div v-if="showDeleteModal" class="modal-overlay" @click.self="showDeleteModal = false">
        <div class="modal-content glass-card" style="max-width: 400px; text-align: center; padding: 2.5rem 2rem;">
            <div style="margin-bottom: 1.5rem;">
                <div
                    style="background: rgba(239, 68, 68, 0.08); width: 70px; height: 70px; border-radius: 50%; display: flex; align-items: center; justify-content: center; margin: 0 auto; border: 1px solid rgba(239, 68, 68, 0.1);">
                    <svg width="34" height="34" viewBox="0 0 24 24" fill="none" stroke="#ef4444" stroke-width="2">
                        <path
                            d="M3 6h18m-2 0v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
                    </svg>
                </div>
            </div>
            <h3 style="color: #0f172a; font-size: 1.5rem; font-weight: 700; margin-bottom: 0.75rem;">
                Delete License?
            </h3>
            <p style="color: #64748b; font-size: 0.95rem; line-height: 1.5; margin-bottom: 2.25rem;">
                Are you sure you want to delete this license? This action is permanent and cannot be undone.
            </p>
            <div class="modal-actions" style="display: flex; gap: 12px;">
                <button class="btn-secondary" @click="showDeleteModal = false"
                    style="flex: 1; border: 1px solid #e2e8f0; border-radius: 12px; padding: 0.85rem; font-weight: 600; color: #64748b; background: #fff; cursor: pointer; transition: all 0.2s;">
                    Cancel
                </button>
                <button class="btn-primary" @click="confirmDelete" :disabled="loading"
                    style="flex: 1; border: none; border-radius: 12px; padding: 0.85rem; font-weight: 700; color: #fff; background: #ef4444; cursor: pointer; display: flex; align-items: center; justify-content: center; gap: 8px; transition: all 0.2s;">
                    <span v-if="loading" class="spinner-small"
                        style="width: 16px; height: 16px; border: 2px solid rgba(255,255,255,0.3); border-top-color: #fff; border-radius: 50%; display: inline-block; animation: spin 0.8s linear infinite;"></span>
                    {{ loading ? 'Deleting...' : 'Yes, Delete' }}
                </button>
            </div>
        </div>
    </div>

    <!-- Role Confirmation Modal -->
    <div v-if="showRoleChangeConfirm" class="modal-overlay" @click.self="cancelRoleChange">
        <div class="modal-content glass-card" style="max-width: 420px; text-align: center; padding: 2.5rem 2rem;">
            <div style="margin-bottom: 1.5rem;">
                <div style="background: rgba(0, 113, 197, 0.08); width: 70px; height: 70px; border-radius: 50%; display: flex; align-items: center; justify-content: center; margin: 0 auto; border: 1px solid rgba(0, 113, 197, 0.1);">
                    <svg width="34" height="34" viewBox="0 0 24 24" fill="none" stroke="#0071C5" stroke-width="2">
                        <path d="M16 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
                        <circle cx="8.5" cy="7" r="4" />
                        <polyline points="17 11 19 13 23 9" />
                    </svg>
                </div>
            </div>
            <h3 style="color: #0f172a; font-size: 1.5rem; font-weight: 700; margin-bottom: 0.75rem;">
                Change User Role?
            </h3>
            <p style="color: #64748b; font-size: 0.95rem; line-height: 1.5; margin-bottom: 2.25rem;">
                Are you sure you want to change role of <b style="color:#0f172a">{{ pendingUser?.name }}</b> to <span style="background: #e2e8f0; padding: 2px 8px; border-radius: 4px; font-weight: 700; color: #0071C5;">{{ pendingRole?.toUpperCase() }}</span>?
            </p>
            <div class="modal-actions" style="display: flex; gap: 12px;">
                <button class="btn-secondary" @click="cancelRoleChange"
                    style="flex: 1; border: 1px solid #e2e8f0; border-radius: 12px; padding: 0.85rem; font-weight: 600; color: #64748b; background: #fff; cursor: pointer; transition: all 0.2s;">
                    No, Cancel
                </button>
                <button class="btn-primary" @click="confirmRoleChange" :disabled="loading"
                    style="flex: 1; border: none; border-radius: 12px; padding: 0.85rem; font-weight: 700; color: #fff; background: #0071C5; cursor: pointer; display: flex; align-items: center; justify-content: center; gap: 8px; transition: all 0.2s; box-shadow: 0 4px 12px rgba(0, 113, 197, 0.2);">
                    <span v-if="loading" class="spinner-small"
                        style="width: 16px; height: 16px; border: 2px solid rgba(255,255,255,0.3); border-top-color: #fff; border-radius: 50%; display: inline-block; animation: spin 0.8s linear infinite;"></span>
                    {{ loading ? 'Updating...' : 'Yes, Change' }}
                </button>
            </div>
        </div>
    </div>
</template>

<script>
import axios from 'axios';
import AppPagination from './AppPagination.vue';

export default {
    name: 'CompanyDetail',
    components: { AppPagination },
    data() {
        return {
            companyId: this.$route.params.id,
            company: {},
            licenses: [],
            users: [],
            activeTab: 'users',
            showModal: false,
            loading: false,
            licenseCount: 1,
            licenseRole: 2, // default to User role ID
            showDeleteModal: false,
            licenseToDelete: null,
            showEditUserModal: false,
            showCreateUserModal: false,
            showNewPassword: false,
            createUserError: '',
            availableLicense: null,
            newUser: { name: '', email: '', mobile: '', password: '', confirmPassword: '' },
            showPassword: false,
            validationError: '',
            successMessage: '',
            errorMessage: '',
            strengthScore: 0,
            strengthLabel: '',
            strengthColor: '#e2e8f0',
            strengthPercent: 0,
            selectedUser: { id: null, name: '', email: '', mobile: '', password: '', confirmPassword: '', role: 'user' },
            usersPage: 1,
            usersTotalPages: 1,
            licensesPage: 1,
            licensesTotalPages: 1,
            availableRoles: [],
            showRoleChangeConfirm: false,
            pendingUser: null,
            pendingRole: null
        }
    },
    watch: {
        'selectedUser.password'(newVal) {
            if (newVal) this.validatePasswordStrength();
            else this.resetStrength();
        }
    },
    async mounted() {
        await this.fetchCompanyDetail();
        await this.fetchLicenses();
        await this.fetchUsers();
        await this.fetchRoles();
    },
    methods: {
        showError(message) {
            this.errorMessage = message;
            setTimeout(() => { this.errorMessage = ''; }, 5000);
        },
        async fetchCompanyDetail() {
            try {
                const response = await axios.get(`${this.$API_URL}/super_admin/company/${this.companyId}`, {
                    headers: { Authorization: `Bearer ${localStorage.getItem('super_admin_token')}` }
                });
                this.company = response.data;
            } catch (err) { console.error(err); }
        },
        async fetchUsers(page = 1) {
            try {
                this.usersPage = page;
                const response = await axios.get(`${this.$API_URL}/super_admin/company/${this.companyId}/users?page=${page}&page_size=10`, {
                    headers: { Authorization: `Bearer ${localStorage.getItem('super_admin_token')}` }
                });
                this.users = response.data.items || [];
                this.usersTotalPages = response.data.total_pages || 1;
            } catch (err) { console.error(err); }
        },
        async fetchRoles() {
            try {
                const response = await axios.get(`${this.$API_URL}/super_admin/roles`, {
                    headers: { Authorization: `Bearer ${localStorage.getItem('super_admin_token')}` }
                });
                this.availableRoles = response.data || [];
            } catch (err) { console.error('Error fetching roles:', err); }
        },
        async fetchLicenses(page = 1) {
            try {
                this.licensesPage = page;
                const response = await axios.get(`${this.$API_URL}/super_admin/company/${this.companyId}/licenses?page=${page}&page_size=10`, {
                    headers: { Authorization: `Bearer ${localStorage.getItem('super_admin_token')}` }
                });
                this.licenses = response.data.items || [];
                this.licensesTotalPages = response.data.total_pages || 1;
            } catch (err) { console.error(err); }
        },
        async generateLicenses() {
            if (this.loading) return;
            this.loading = true;
            try {
                await axios.post(`${this.$API_URL}/company/create-license`, {
                    company_id: this.companyId,
                    total_license: this.licenseCount,
                    role: this.licenseRole
                }, {
                    headers: { Authorization: `Bearer ${localStorage.getItem('super_admin_token')}` }
                });
                this.showModal = false;
                await this.fetchLicenses();
                this.licenseCount = 1;
                this.licenseRole = 2;
            } catch (err) {
                this.showError(err.response?.data?.message || err.response?.data?.detail || 'License generation failed.');
            }
            finally { this.loading = false; }
        },
        async openCreateUser() {
            this.newUser = { name: '', email: '', mobile: '', password: '', confirmPassword: '' };
            this.createUserError = '';
            this.showNewPassword = false;
            this.availableLicense = null;
            this.showCreateUserModal = true;
            // Show which key will be consumed. The backend picks the newest unused
            // license itself — this is only so the admin sees it up front.
            await this.fetchAvailableLicense();
        },
        async fetchAvailableLicense() {
            try {
                const response = await axios.get(`${this.$API_URL}/super_admin/company/${this.companyId}/licenses?page=1&page_size=100`, {
                    headers: { Authorization: `Bearer ${localStorage.getItem('super_admin_token')}` }
                });
                const unused = (response.data.items || []).filter(l => l.is_active && !l.user);
                // Highest id = newest, which is the key the backend will pick.
                this.availableLicense = unused.length
                    ? unused.reduce((a, b) => (b.id > a.id ? b : a))
                    : null;
            } catch (err) {
                console.error('Error fetching available license:', err);
                this.availableLicense = null;
            }
        },
        async createUser() {
            if (this.loading) return;
            this.createUserError = '';

            if (!this.newUser.name.trim()) {
                this.createUserError = 'User name is required.';
                return;
            }
            if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(this.newUser.email)) {
                this.createUserError = 'Please enter a valid email address.';
                return;
            }
            if (!/^[0-9]{10}$/.test(this.newUser.mobile)) {
                this.createUserError = 'Mobile number must be exactly 10 digits.';
                return;
            }
            if (this.newUser.password.length < 8) {
                this.createUserError = 'Password must be at least 8 characters.';
                return;
            }
            if (this.newUser.password !== this.newUser.confirmPassword) {
                this.createUserError = 'Passwords do not match. Please verify.';
                return;
            }

            this.loading = true;
            try {
                const response = await axios.post(`${this.$API_URL}/super_admin/company/${this.companyId}/user`, {
                    name: this.newUser.name.trim(),
                    email: this.newUser.email.trim(),
                    mobile: this.newUser.mobile.trim(),
                    password: this.newUser.password
                }, {
                    headers: { Authorization: `Bearer ${localStorage.getItem('super_admin_token')}` }
                });
                this.showCreateUserModal = false;
                // A license was consumed, so both tables need refreshing.
                await this.fetchUsers(this.usersPage);
                await this.fetchLicenses(this.licensesPage);

                this.successMessage = `User created successfully. License ${response.data.license_used} consumed.`;
                setTimeout(() => { this.successMessage = ''; }, 4000);
            } catch (err) {
                this.createUserError = err.response?.data?.message || err.response?.data?.detail || 'User creation failed.';
            }
            finally { this.loading = false; }
        },
        deleteLicense(licenseId) {
            this.licenseToDelete = licenseId;
            this.showDeleteModal = true;
        },
        editUser(user) {
            this.selectedUser = {
                id: user.id,
                name: user.name,
                email: user.email,
                mobile: user.mobile,
                role: user.roles && user.roles.length > 0 ? user.roles[0].name : 'user',
                password: '',
                confirmPassword: ''
            };
            this.validationError = '';
            this.resetStrength();
            this.showPassword = false;
            this.showEditUserModal = true;
        },
        resetStrength() {
            this.strengthScore = 0;
            this.strengthLabel = '';
            this.strengthColor = '#e2e8f0';
            this.strengthPercent = 0;
            if (this.selectedUser) this.selectedUser.confirmPassword = '';
        },
        validatePasswordStrength() {
            const password = this.selectedUser.password || '';
            let score = 0;
            if (password.length >= 8) score += 1;
            if (password.length >= 12) score += 1;
            if (/[A-Z]/.test(password)) score += 1;
            if (/[0-9]/.test(password)) score += 1;
            if (/[^A-Za-z0-9]/.test(password)) score += 1;
            this.strengthScore = score;
            if (score <= 2) {
                this.strengthLabel = 'Weak';
                this.strengthColor = '#ef4444';
                this.strengthPercent = 33;
            } else if (score <= 4) {
                this.strengthLabel = 'Medium';
                this.strengthColor = '#eab308';
                this.strengthPercent = 66;
            } else {
                this.strengthLabel = 'Strong';
                this.strengthColor = '#22c55e';
                this.strengthPercent = 100;
            }
        },
        async updateUser() {
            if (this.loading) return;
            this.validationError = '';

            // 1. Basic Fields Validation
            if (!this.selectedUser.name.trim()) {
                this.validationError = "User name is required";
                return;
            }
            if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(this.selectedUser.email)) {
                this.validationError = "Please enter a valid email address";
                return;
            }
            if (this.selectedUser.mobile && !/^[0-9]{10}$/.test(this.selectedUser.mobile)) {
                this.validationError = "Mobile number must be exactly 10 digits";
                return;
            }

            // 2. Password Strength & Match Validation (Matching User Panel)
            if (this.selectedUser.password) {
                if (this.strengthScore < 3) {
                    this.validationError = "Password is too weak. Must be Medium or Strong.";
                    return;
                }
                if (this.selectedUser.password !== this.selectedUser.confirmPassword) {
                    this.validationError = "Passwords do not match. Please verify.";
                    return;
                }
            }

            this.loading = true;
            try {
                await axios.put(`${this.$API_URL}/super_admin/user/${this.selectedUser.id}`, null, {
                    params: {
                        name: this.selectedUser.name,
                        email: this.selectedUser.email,
                        mobile: this.selectedUser.mobile,
                        role_name: this.selectedUser.role,
                        password: this.selectedUser.password || undefined
                    },
                    headers: { Authorization: `Bearer ${localStorage.getItem('super_admin_token')}` }
                });
                this.showEditUserModal = false;
                await this.fetchUsers();

                // Custom Success Message instead of Alert
                this.successMessage = 'User details updated successfully';
                setTimeout(() => {
                    this.successMessage = '';
                }, 3000);

            } catch (err) {
                console.error(err);
                this.validationError = err.response?.data?.detail || 'Failed to update user';
            } finally {
                this.loading = false;
            }
        },
        async quickUpdateRole(user, newRoleName) {
            // Store details for confirmation popup
            this.pendingUser = user;
            this.pendingRole = newRoleName;
            this.showRoleChangeConfirm = true;
        },
        async confirmRoleChange() {
            if (this.loading || !this.pendingUser) return;
            
            this.loading = true;
            try {
                await axios.put(`${this.$API_URL}/super_admin/user/${this.pendingUser.id}`, null, {
                    params: {
                        name: this.pendingUser.name,
                        email: this.pendingUser.email,
                        mobile: this.pendingUser.mobile,
                        role_name: this.pendingRole
                    },
                    headers: { Authorization: `Bearer ${localStorage.getItem('super_admin_token')}` }
                });
                this.successMessage = `Account for ${this.pendingUser.name} updated successfully`;
                setTimeout(() => { this.successMessage = ''; }, 3000);
            } catch (err) {
                console.error(err);
                this.showError(err.response?.data?.message || err.response?.data?.detail || 'Failed to update role');
            } finally {
                this.loading = false;
                this.showRoleChangeConfirm = false;
                this.pendingUser = null;
                this.pendingRole = null;
                await this.fetchUsers(this.usersPage);
            }
        },
        async cancelRoleChange() {
            this.showRoleChangeConfirm = false;
            this.pendingUser = null;
            this.pendingRole = null;
            await this.fetchUsers(this.usersPage); // Revert dropdown value
        },
        async confirmDelete() {
            if (!this.licenseToDelete || this.loading) return;

            this.loading = true;
            try {
                await axios.delete(`${this.$API_URL}/super_admin/license/${this.licenseToDelete}`, {
                    headers: { Authorization: `Bearer ${localStorage.getItem('super_admin_token')}` }
                });
                this.showDeleteModal = false;
                await this.fetchLicenses();
            } catch (err) {
                console.error(err);
                this.showError(err.response?.data?.message || err.response?.data?.detail || 'Failed to delete license');
            } finally {
                this.loading = false;
            }
        },
        formatDate(dateString) {
            if (!dateString) return 'Not Recorded';
            try {
                const date = new Date(dateString);
                if (isNaN(date.getTime())) return 'Not Recorded';
                return date.toLocaleDateString('en-IN', {
                    day: '2-digit', month: 'short', year: 'numeric'
                }) + ', ' + date.toLocaleTimeString('en-IN', {
                    hour: '2-digit', minute: '2-digit', hour12: true
                });
            } catch (e) { return 'Not Recorded'; }
        }
    }
}
</script>

<style scoped>
.breadcrumb-cd {
    margin-bottom: 1.5rem;
    display: flex;
    align-items: center;
    gap: 8px;
    font-size: var(--fs-sm);
}

.breadcrumb-cd a {
    color: var(--text-secondary);
    text-decoration: none;
    font-weight: 500;
    transition: color 0.2s;
}

.breadcrumb-cd a:hover {
    color: #0071C5;
}

.crumb-sep { color: #cbd5e1; }
.crumb-current { color: #0071C5; font-weight: 700; }

.cd-header {
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    flex-wrap: wrap;
    gap: 1rem;
    margin-bottom: 2rem;
}

.cd-title {
    margin: 0;
    font-family: var(--font-display);
    font-size: var(--fs-h1);
    line-height: var(--lh-h1);
    font-weight: var(--fw-h1);
    color: var(--text-primary);
    letter-spacing: -0.02em;
}

.cd-subtitle {
    color: var(--text-secondary);
    margin: var(--space-heading-body) 0 0;
    font-size: var(--fs-sm);
}

.btn-generate-licenses {
    display: flex;
    align-items: center;
    gap: 0.4rem;
    background: linear-gradient(90deg, #0071C5, #0084FF);
    color: #fff;
    border: none;
    padding: 0.7rem 1.4rem;
    border-radius: 12px;
    font-weight: 700;
    font-size: var(--fs-btn);
    font-family: var(--font-sans);
    cursor: pointer;
    box-shadow: 0 6px 16px rgba(0, 113, 197, 0.35);
    transition: all 0.2s;
    white-space: nowrap;
}

.btn-generate-licenses:hover {
    transform: translateY(-2px);
    box-shadow: 0 10px 22px rgba(0, 113, 197, 0.45);
}

/* Tabs — glass command dock, matching bid_ui's phase toggle */
.tabs-dock-cd {
    display: inline-flex;
    background: rgba(15, 36, 64, 0.04);
    padding: 6px;
    border-radius: 14px;
    gap: 4px;
    margin-bottom: 2rem;
}

.tab-dock-btn-cd {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    border: none;
    border-radius: 10px;
    background: transparent;
    color: var(--text-secondary);
    font-family: var(--font-sans);
    font-size: var(--fs-sm);
    font-weight: 700;
    padding: 8px 18px;
    cursor: pointer;
    transition: all 0.2s;
}

.tab-dock-btn-cd:hover {
    color: var(--text-primary);
}

.tab-dock-btn-cd.active {
    background: #ffffff;
    color: #0071C5;
    box-shadow: 0 4px 12px rgba(15, 23, 42, 0.08);
}

.modal-overlay {
    position: fixed;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background: rgba(0, 0, 0, 0.4);
    display: flex;
    justify-content: center;
    align-items: center;
    z-index: 1000;
    backdrop-filter: blur(4px);
}

.modal-content {
    background: white;
    padding: 2rem;
    border-radius: 16px;
    width: 90%;
    max-width: 450px;
    box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.1);
}

.modal-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 1.5rem;
}

.close-btn {
    background: none;
    border: none;
    font-size: 1.5rem;
    cursor: pointer;
    color: #94a3b8;
}

.btn-secondary {
    padding: 0.6rem 1.2rem;
    border: 1px solid #e2e8f0;
    border-radius: 8px;
    background: white;
    color: #64748b;
    font-weight: 600;
    cursor: pointer;
}

@keyframes spin {
    from {
        transform: rotate(0deg);
    }

    to {
        transform: rotate(360deg);
    }
}

/* Success Toast Styles */
.success-toast {
    position: fixed;
    top: 24px;
    left: 50%;
    transform: translateX(-50%);
    z-index: 9999;
    animation: toastSlideDown 0.4s cubic-bezier(0.16, 1, 0.3, 1);
}

.toast-content {
    background: #10b981;
    color: white;
    padding: 12px 24px;
    border-radius: 12px;
    display: flex;
    align-items: center;
    gap: 12px;
    box-shadow: 0 10px 25px -5px rgba(16, 185, 129, 0.3);
    font-weight: 600;
}

@keyframes toastSlideDown {
    from {
        transform: translate(-50%, -40px);
        opacity: 0;
    }

    to {
        transform: translate(-50%, 0);
        opacity: 1;
    }
}

/* Error Toast Styles */
.error-toast {
    position: fixed;
    top: 24px;
    left: 50%;
    transform: translateX(-50%);
    z-index: 9999;
    animation: toastSlideDown 0.4s cubic-bezier(0.16, 1, 0.3, 1);
    max-width: 90vw;
}

.toast-content.error {
    background: #ef4444;
    box-shadow: 0 10px 25px -5px rgba(239, 68, 68, 0.35);
}

.toast-close-btn {
    background: none;
    border: none;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 2px;
    margin-left: 4px;
    border-radius: 4px;
    opacity: 0.85;
    flex-shrink: 0;
}

.toast-close-btn:hover {
    opacity: 1;
    background: rgba(255, 255, 255, 0.15);
}

/* Shake animation for validation errors */
@keyframes shake {

    10%,
    90% {
        transform: translate3d(-1px, 0, 0);
    }

    20%,
    80% {
        transform: translate3d(2px, 0, 0);
    }

    30%,
    50%,
    70% {
        transform: translate3d(-4px, 0, 0);
    }

    40%,
    60% {
        transform: translate3d(4px, 0, 0);
    }
}

/* Hide browser default password eye */
input::-ms-reveal,
input::-ms-clear {
    display: none;
}
</style>
